import { container } from 'inversify.config';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Button, Dimmer, Loader, Modal } from 'semantic-ui-react';
import { IdentityService, Roles } from 'services/identity-service';
import { EventFormStore, EventFormViewModel } from 'stores/events/event-form-store';
import {
  CreateFeedbackFormDto,
  EventFeedbackTabStore,
  NewFeedbackFormUserStore,
  Respondent,
  SimpleFeedbackFormAnswerDto
} from 'stores/events/feedback-forms-store';
import { UserDto } from 'stores/users/users-store';
import { isCurrentUserEventInstructor, isUserEventOwner } from 'utils/event-utils';
import { openInNewWindow } from 'utils/useful-functions';
import TailwindChooseUser from 'widgets/form/choose-user-form-tw';
import TailwindFeedbackTable from '../../../events/event-form/tabs/feedback/feedback-table-form-tw';

interface EventFeedBackContainerProps {
  selectedEventFormViewModel: EventFormViewModel;
  eventFormStore?: EventFormStore;
  feedbacksStore?: EventFeedbackTabStore;
  newFeedbackStore?: NewFeedbackFormUserStore;
}

const EventFeedBackContainer: React.FC<EventFeedBackContainerProps> = ({
  selectedEventFormViewModel,
  eventFormStore,
  feedbacksStore,
  newFeedbackStore
}) => {
  const { t } = useTranslation();
  const identityService: IdentityService = container.get(IdentityService);
  const currentUserInfo = identityService.getUserInfo();

  const [showConfirmFeedbackModal, setShowConfirmFeedbackModal] = useState<boolean>(false);
  const [showConfirmDeleteUserFeedback, setShowConfirmDeleteUserFeedback] = useState<boolean>(false);
  const [showUsersModal, setShowUsersModal] = useState<boolean>(false);
  const [feedbackTemplateIdToGenerate, setFeedbackTemplateIdToGenerate] = useState<string>(null);
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const [isTraineesFeedback, setIsTraineesFeedback] = useState<boolean>(false);
  const [feedbackFormAnswerClickedToDeleteId, setFeedbackFormAnswerClickedToDeleteId] = useState<string>(null);

  const isCurrentUserEventOwnerOrInstructorOrAdmin = (): boolean => {
    const event = selectedEventFormViewModel; //eventFormStore.state.item;
    const a = isUserEventOwner(event);
    const b = identityService.isInRole(Roles.Instructor);
    const c = isCurrentUserEventInstructor(event);
    const d = identityService.isInRole(Roles.Admin);
    return a || (b && c) || d;
  };

  const isCurrentUserPlannerOrPoC = (): boolean => {
    return identityService.isInRole(Roles.Planner) || identityService.isInRole(Roles.PlannerMTC) || identityService.isInRole(Roles.PoC);
  };

  const handleOnRemoveFeedBack = (feedbackFormAnswerClicked: SimpleFeedbackFormAnswerDto) => {
    setShowConfirmDeleteUserFeedback(true);
    setFeedbackFormAnswerClickedToDeleteId(feedbackFormAnswerClicked.id);
  };

  const onOpenEmployeeFeedBackForm = (form: SimpleFeedbackFormAnswerDto) => {
    openInNewWindow(`./assessments/feedbacks/form/${form.id}`);
  };

  const isEventClosed = (): boolean => {
    //return eventFormStore.state.item.status.toString() === 'Closed';
    return selectedEventFormViewModel.status.toString() === 'Closed';
  };

  const toggleUsersModal = () => {
    const showUsersModalCopy = !showUsersModal;
    setShowUsersModal(showUsersModalCopy);
  };

  const handleGenerateForms = (respondent: string, templateId: string) => {
    setFeedbackTemplateIdToGenerate(templateId);
    if (Respondent.Trainees === Respondent[respondent]) {
      setIsTraineesFeedback(true);
      setShowConfirmFeedbackModal(true);
    } else if (Respondent.ManualAssignment === Respondent[respondent]) {
      toggleUsersModal();
      setIsTraineesFeedback(false);
    }
  };

  const isCurrentUserEmployee = (): boolean => {
    return identityService.isInRole(Roles.Employee);
  };

  const getCurrentUserId = (): string => {
    const { userId } = currentUserInfo;
    return userId;
  };

  const closeConfirmModal = () => {
    setShowConfirmFeedbackModal(false);
  };

  const canSeeUsersMessage = (): boolean => {
    if (selectedUsers && selectedUsers.length > 0 && !isTraineesFeedback) return true;
    return false;
  };

  const load = useCallback(async () => {
    try {
      // feedbacksStore.getTemplatesByEventId(eventFormStore.state.item.id);
      feedbacksStore.getTemplatesByEventId(selectedEventFormViewModel.id);
    } catch (error) {
      console.error({ error });
    }
  }, [feedbacksStore, selectedEventFormViewModel.id]);

  useEffect(() => {
    load();
  }, [load]);

  const onGenerateFeedback = async () => {
    setShowConfirmFeedbackModal(false);
    setShowUsersModal(false);
    setShowConfirmDeleteUserFeedback(false);

    const { id: eventId, hasStudents, students } = selectedEventFormViewModel; //eventFormStore.state.item;
    const storedData = feedbacksStore.state.items.find(({ item }) => item.id === feedbackTemplateIdToGenerate);

    const isTrainees = storedData.item.respondents === 'Trainees';
    let isValid = (isTrainees && (!hasStudents || (students || []).length > 0)) || !isTrainees;
    if (!isValid) {
      eventFormStore.changeMessages([{ body: 'Number of assigned student should be greater than 0', level: 'Error' }]);
      return;
    }

    if (!storedData.item) return;
    const newItem: CreateFeedbackFormDto = {
      eventId,
      usersId: (selectedUsers || []).map(({ id }) => id),
      feedbackTemplateId: storedData.item.id
    };

    newFeedbackStore.createNew(newItem);
    try {
      const newForms = await newFeedbackStore.createForms();
      if (newForms && newForms.isSuccess) {
        feedbacksStore.state.items = [];
        load();
      }
    } catch (error) {
      console.error({ error });
    }

    setShowConfirmFeedbackModal(false);
    setShowUsersModal(false);
    setShowConfirmDeleteUserFeedback(false);
  };

  const handleAddUsers = (users: UserDto[]) => {
    setSelectedUsers(users);
    setShowConfirmFeedbackModal(true);
  };

  const closeConfirmDeleteModal = () => {
    setShowConfirmDeleteUserFeedback(false);
    setFeedbackFormAnswerClickedToDeleteId(null);
  };

  const handleConfirmationRemoveFeedBack = async () => {
    await feedbacksStore.deleteAsync(feedbackFormAnswerClickedToDeleteId);
    setShowConfirmDeleteUserFeedback(false);
    setFeedbackFormAnswerClickedToDeleteId(null);
    feedbacksStore.state.items = [];
    load();
  };

  const { items } = feedbacksStore.state;
  const isEventOwnerOrInstructorOrAdmin = isCurrentUserEventOwnerOrInstructorOrAdmin();
  const isPocOrPlanner = isCurrentUserPlannerOrPoC();
  const isUserEmployee = isCurrentUserEmployee();
  const currentUserId = getCurrentUserId();

  return (
    <div className="space-y-4">
      <Dimmer
        active={feedbacksStore.state.isBusy || newFeedbackStore.state.isBusy}
        style={{ zIndex: 999, background: 'rgba(0, 0, 0, 0.4)' }}
      >
        <Loader indeterminate>{t('Loading')}</Loader>
      </Dimmer>

      {(items || []).map(({ item }) => {
        const { title, feedbackFormAnswers, id, respondents } = item;
        const feedbackTypeClass =
          (feedbackFormAnswers || []).length > 0 ? 'form__feedback-item flex-column' : 'form__feedback-item flex-start-center';

        return (
          <>
            {(isEventOwnerOrInstructorOrAdmin || isPocOrPlanner) && feedbackFormAnswers && feedbackFormAnswers.length > 0 && (
              <div key={id} className={feedbackTypeClass}>
                <p className="feedback-tab__item-title">{title}</p>
                <TailwindFeedbackTable
                  templateId={id}
                  forms={feedbackFormAnswers && feedbackFormAnswers}
                  handleOnRemoveFeedBack={handleOnRemoveFeedBack}
                  onOpenEmployeeFeedBackForm={onOpenEmployeeFeedBackForm}
                  isEventClosed={isEventClosed()}
                  canRemoveFeedback={isEventOwnerOrInstructorOrAdmin}
                />
              </div>
            )}

            {isEventOwnerOrInstructorOrAdmin &&
              item &&
              !isEventClosed() &&
              (!feedbackFormAnswers || (feedbackFormAnswers || []).length <= 0) && (
                <div key={`${id}_generate`} className="mb-3 flex items-center justify-between gap-4">
                  <p className="text-sm text-gray-700 flex-grow">{title}</p>
                  <button
                    data-modal-target="popup-modal"
                    data-modal-toggle="popup-modal"
                    onClick={() => handleGenerateForms(respondents, id)}
                    className="bg-primary-600 text-white py-2 px-4 rounded whitespace-nowrap"
                    type="button"
                  >
                    {t('Generate Feedback')}
                  </button>
                </div>
              )}

            {isUserEmployee &&
              !isEventOwnerOrInstructorOrAdmin &&
              (feedbackFormAnswers || [])
                .filter(answer => answer.userId === currentUserId)
                .map(answer => (
                  <div key={`${id}_answer`} className="mb-3 flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-700 flex-grow">{title}</p>
                    <Button
                      onClick={() => onOpenEmployeeFeedBackForm(answer)}
                      className={answer.answered ? 'feedback-generation__answered-btn' : ''}
                      disabled={answer.answered}
                    >
                      {answer.answered ? t('Answered') : t('Answer Feedback')}
                    </Button>
                    {/* <button
                      onClick={() => onOpenEmployeeFeedBackForm(answer)}
                      className={`${answer.answered ? 'bg-grey-600' : 'bg-primary-600'} text-white py-2 px-4 rounded whitespace-nowrap`}
                      type="button"
                      disabled={answer.answered}
                    >
                      {answer.answered ? t('Answered') : t('Answer Feedback')}
                    </button> */}
                  </div>
                ))}
          </>
        );
      })}

      {showConfirmFeedbackModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-end min-h-screen p-4 text-center sm:p-0 mr-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={closeConfirmModal}
            ></div>
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 max-w-sm w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-black-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {t('Generate Feedback')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t(
                          `This Feedback will be generated for ${
                            canSeeUsersMessage() ? 'the users selected' : 'all the Trainees'
                          }. Do you want to generate it?`
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={onGenerateFeedback}
                >
                  {t('Accept')}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeConfirmModal}
                >
                  {t('Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUsersModal && (
        <TailwindChooseUser
          alreadySelectedUserIds={selectedUsers && selectedUsers.map(x => x.id)}
          users={selectedUsers}
          onAddUsers={handleAddUsers}
          onCloseUsersModal={toggleUsersModal}
          showEmployeeIdFilter
          showPositionCodeFilter
          showRoleFilter
          showLocationFilter
          acceptBtnTitle="Select"
        />
      )}

      {showConfirmDeleteUserFeedback && (
        <Modal
          style={{ transform: 'translateY(35vh)' }}
          centered
          open={showConfirmDeleteUserFeedback}
          size="mini"
          className="have-warnings-popup feedback__confirm-modal"
          closeOnEscape={true}
          onClose={closeConfirmDeleteModal}
        >
          <Modal.Content className="have-warnings-popup__content">
            <p>{t(`This User Feedback will be deleted. Are you sure you want to continue?`)}</p>
          </Modal.Content>
          <div className="have-warnings-popup__buttons-container">
            <button onClick={closeConfirmDeleteModal} className="bg-white-600 text-blue py-2 px-4 rounded whitespace-nowrap" type="button">
              {t('Cancel')}
            </button>
            <button
              onClick={handleConfirmationRemoveFeedBack}
              className="bg-primary-600 text-white py-2 px-4 rounded whitespace-nowrap"
              type="button"
            >
              {t('Accept')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default connect(
  ['eventFormStore', EventFormStore],
  ['feedbacksStore', EventFeedbackTabStore],
  ['newFeedbackStore', NewFeedbackFormUserStore]
)(EventFeedBackContainer);
